import { useMemo, forwardRef } from "react";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HeatmapModule from "highcharts/modules/heatmap";
import SankeyModule from "highcharts/modules/sankey";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { chartThemes, heatmapColors } from "./chartColors";
import type { ChartTheme } from "./chartColors";
import type {
  ChartProps,
  ChartRef,
  DonutChartProps,
  HeatmapChartProps,
  LineBarColumnProps,
  SankeyChartProps,
} from "./chartTypes";

export type {
  ChartProps,
  ChartRef,
  HeatmapCell,
  SankeyLink,
  SankeyNode,
  SeriesData,
} from "./chartTypes";
export type { ChartTheme };

HeatmapModule(Highcharts);
SankeyModule(Highcharts);

const baseChartOptions: Highcharts.Options = {
  chart: {
    style: {
      fontFamily: "inherit",
    },
  },
  title: {
    text: "",
    align: "left",
    style: {
      fontSize: "16px",
      fontWeight: "600",
    },
  },
  subtitle: {
    text: "",
    align: "left",
  },
  credits: {
    enabled: false,
  },
  legend: {
    align: "left",
    verticalAlign: "bottom",
    itemStyle: {
      fontWeight: "normal",
    },
  },
  tooltip: {
    shared: true,
    useHTML: true,
  },
};

function getLineBarOptions(props: LineBarColumnProps): Highcharts.Options {
  const { type, series, categories, xAxisTitle, yAxisTitle, onPointClick } =
    props;
  const stacked = props.type === "column" ? props.stacked : undefined;

  return {
    chart: {
      type: type === "bar" ? "bar" : type === "column" ? "column" : "line",
    },
    xAxis: {
      categories,
      title: { text: xAxisTitle || "" },
      crosshair: true,
    },
    yAxis: {
      title: { text: yAxisTitle || "" },
      stackLabels: stacked ? { enabled: true } : undefined,
    },
    plotOptions: {
      series: {
        stacking: stacked ? "normal" : undefined,
        cursor: onPointClick ? "pointer" : "default",
        borderWidth: 0,
        point: {
          events: {
            click: function () {
              if (onPointClick) {
                const point = this as Highcharts.Point;
                onPointClick({
                  x: point.category || point.x,
                  y: point.y ?? 0,
                  seriesName: point.series.name,
                });
              }
            },
          },
        },
      },
    },
    series: series?.map((s) => ({
      type: type === "bar" ? "bar" : type === "column" ? "column" : "line",
      name: s.name,
      data: s.data as number[],
      color: s.color,
      colorByPoint:
        (type === "bar" || type === "column") && series?.length === 1,
    })) as Highcharts.SeriesOptionsType[],
  };
}

function getSankeyOptions(props: SankeyChartProps): Highcharts.Options {
  const { sankeyNodes, sankeyLinks, onNodeClick } = props;
  type SankeyPoint = Highcharts.Point & {
    isNode?: boolean;
    id?: string;
    from?: string;
    to?: string;
  };

  const resetSankeyEmphasis = (series: Highcharts.Series) => {
    const points = series.points as SankeyPoint[];
    for (const point of points) {
      point.graphic?.attr({
        opacity: point.isNode ? 1 : 0.35,
      });
      if (point.isNode) {
        point.graphic?.attr({
          stroke: "none",
          "stroke-width": 0,
        });
      }
    }
    if (series.chart.container.style.cursor !== "default") {
      series.chart.container.style.cursor = "default";
    }
  };

  const applySankeyNodeEmphasis = (focused: SankeyPoint) => {
    const points = focused.series.points as SankeyPoint[];
    const focusID = focused.id;
    if (!focusID) return;

    for (const point of points) {
      if (point.isNode) {
        const isFocusedNode = point.id === focusID;
        const isConnectedNode = points.some(
          (other) =>
            !other.isNode &&
            (other.from === focusID || other.to === focusID) &&
            (other.from === point.id || other.to === point.id),
        );
        point.graphic?.attr({
          opacity: isFocusedNode ? 1 : isConnectedNode ? 0.7 : 0.25,
          stroke: isFocusedNode ? "#111827" : "none",
          "stroke-width": isFocusedNode ? 2 : 0,
        });
        continue;
      }

      const isConnectedLink = point.from === focusID || point.to === focusID;
      point.graphic?.attr({
        opacity: isConnectedLink ? 0.55 : 0.1,
      });
    }

    focused.series.chart.container.style.cursor = onNodeClick
      ? "pointer"
      : "default";
  };

  return {
    chart: {
      animation: {
        duration: 400,
        easing: "swing",
      },
      zooming: {
        type: "xy",
      },
      panning: {
        enabled: true,
        type: "xy",
      },
      panKey: "shift",
    },
    plotOptions: {
      sankey: {
        animation: {
          duration: 400,
        },
        states: {
          inactive: {
            enabled: false,
          },
        },
      },
      series: {
        animation: {
          duration: 400,
        },
        states: {
          inactive: {
            enabled: false,
          },
        },
        point: {
          events: {
            mouseOver: function () {
              const point = this as SankeyPoint;
              if (!point.isNode) return;
              applySankeyNodeEmphasis(point);
            },
            mouseOut: function () {
              const point = this as SankeyPoint;
              if (!point.isNode) return;
              resetSankeyEmphasis(point.series);
            },
          },
        },
      },
    },
    tooltip: {
      shared: false,
      headerFormat: "",
      pointFormatter: function () {
        const p = this as unknown as {
          fromNode?: { name: string; sum?: number };
          toNode?: { name: string };
          weight?: number;
        };
        const fromTotal = p.fromNode?.sum ?? 0;
        const pct =
          fromTotal > 0
            ? (((p.weight ?? 0) / fromTotal) * 100).toFixed(1)
            : "0";
        return `${p.fromNode?.name} → ${p.toNode?.name}: <b>${(p.weight ?? 0).toLocaleString()}</b> (${pct}%)`;
      },
    } as Highcharts.TooltipOptions,
    series: [
      {
        type: "sankey",
        curveFactor: 0.3,
        minLinkWidth: 0,
        nodes: sankeyNodes?.map((node) => ({
          id: node.id,
          name: node.name ?? node.id,
          column: node.column,
          color: node.color,
          cursor: onNodeClick ? "pointer" : "default",
          events: onNodeClick
            ? {
                click: function () {
                  onNodeClick(node.id);
                },
              }
            : undefined,
        })),
        data: sankeyLinks?.map((link) => ({
          from: link.from,
          to: link.to,
          weight: link.weight,
          color: link.color ?? null,
        })),
        name: "Sankey",
        tooltip: {
          nodeFormatter: function () {
            const p = this as unknown as {
              name?: string;
              sum?: number;
              linksFrom?: { weight: number }[];
              linksTo?: { weight: number }[];
            };
            const totalIn = (p.linksTo ?? []).reduce((s, l) => s + l.weight, 0);
            const totalOut = (p.linksFrom ?? []).reduce(
              (s, l) => s + l.weight,
              0,
            );
            const flow = Math.max(totalIn, totalOut);
            return (
              `${p.name}: <b>${(p.sum ?? 0).toLocaleString()}</b>` +
              (flow > 0
                ? `<br/>Total flow: <b>${flow.toLocaleString()}</b>`
                : "")
            );
          },
        },
      },
    ] as Highcharts.SeriesOptionsType[],
  };
}

function getHeatmapOptions(
  props: HeatmapChartProps,
  theme: ChartTheme,
): Highcharts.Options {
  const { heatmapData, xCategories, yCategories, onHeatmapCellClick, styles } =
    props;

  const options: Highcharts.Options = {
    chart: {
      height: Math.max(400, (yCategories?.length ?? 5) * 60),
    },
    xAxis: {
      categories: xCategories,
      opposite: true,
      lineWidth: 0,
      tickWidth: 0,
      labels: {
        useHTML: true,
        formatter: function () {
          const xLabelClassName = styles?.xAxis?.labelClassName ?? "";
          return `<span class="${xLabelClassName}">${(this as unknown as Highcharts.AxisLabelsFormatterContextObject).value}</span>`;
        },
      },
    },
    yAxis: {
      categories: yCategories,
      lineWidth: 0,
      tickWidth: 0,
      title: { text: "" },
      labels: {
        useHTML: true,
        formatter: function () {
          const yLabelClassName = styles?.yAxis?.labelClassName ?? "";
          return `<span class="${yLabelClassName}">${(this as unknown as Highcharts.AxisLabelsFormatterContextObject).value}</span>`;
        },
      },
    },
    colorAxis: {
      stops: Object.values(heatmapColors.stops).map(
        (s) => [s.position, s.color] as [number, string],
      ),
      labels: {
        formatter: function (ctx) {
          if (ctx.isFirst) return "Low";
          if (ctx.isLast) return "High";
          return "";
        },
        style: {
          textOverflow: "none",
        },
      },
    },
    legend: {
      enabled: true,
    },
    tooltip: {
      enabled: true,
      formatter: function () {
        const point = (
          this as unknown as Highcharts.TooltipFormatterContextObject
        ).point as Highcharts.Point & { value?: number };
        return `<b>${point.series.xAxis.categories?.[point.x ?? 0]}</b> / <b>${point.series.yAxis.categories?.[point.y ?? 0]}</b>: ${point.value}`;
      },
    },
    series: [
      {
        type: "heatmap",
        borderWidth: 1,
        borderColor: heatmapColors.borderColor,
        cursor: onHeatmapCellClick ? "pointer" : "default",
        data: heatmapData,
        point: {
          events: {
            click: function () {
              if (onHeatmapCellClick) {
                const point = this as Highcharts.Point & { value?: number };
                const xCategory =
                  point.series.xAxis.categories?.[point.x ?? 0] ?? "";
                const yCategory =
                  point.series.yAxis.categories?.[point.y ?? 0] ?? "";
                onHeatmapCellClick({
                  x: xCategory,
                  y: yCategory,
                  value: point.value ?? 0,
                });
              }
            },
          },
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: "1rem",
            fontWeight: "normal",
            textOutline: "none",
            color: heatmapColors.labelColor[theme],
          },
          formatter: function () {
            const point = this.point as Highcharts.Point & { value?: number };
            return String(point.value ?? 0);
          },
        },
        name: "Heatmap",
      },
    ] as Highcharts.SeriesOptionsType[],
  };

  if (styles?.chart?.backgroundColor) {
    options.chart!.backgroundColor = styles.chart.backgroundColor;
  }

  return options;
}

function getDonutOptions(props: DonutChartProps): Highcharts.Options {
  return {
    chart: {
      type: "pie",
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        type: "pie",
        innerSize: "75%",
        data: props.series.map((series) => [
          series.name,
          (series.data as number[])[0],
        ]),
      },
    ],
  };
}

export const Chart = forwardRef<ChartRef, ChartProps>(
  function Chart(chartProps, ref) {
    const {
      theme = "light",
      title,
      subtitle,
      height,
      width,
      showLegend = true,
      showTooltip = true,
      colors,
      className,
      topContent,
      topContentClassName,
      options: customOptions,
    } = chartProps;

    const themeColors = chartThemes[theme];
    const seriesColors = colors ?? themeColors.series;

    const options = useMemo<Highcharts.Options>(() => {
      let typeOptions: Highcharts.Options = {};

      switch (chartProps.type) {
        case "line":
        case "bar":
        case "column":
          typeOptions = getLineBarOptions(chartProps);
          break;
        case "donut":
          typeOptions = getDonutOptions(chartProps);
          break;
        case "sankey":
          typeOptions = getSankeyOptions(chartProps);
          break;
        case "heatmap":
          typeOptions = getHeatmapOptions(chartProps, theme);
          break;
      }

      // Spread `customOptions` first so it can contribute top-level keys
      // (e.g. `series`, `colors`), then re-apply the carefully-merged
      // structured keys after so they shallow-merge with `customOptions`
      // instead of being replaced by it.
      const mergedOptions: Highcharts.Options = {
        ...baseChartOptions,
        ...typeOptions,
        colors: seriesColors,
        ...customOptions,
        chart: {
          backgroundColor: themeColors.background,
          height: height,
          width: typeof width === "number" ? width : undefined,
          ...baseChartOptions.chart,
          ...typeOptions.chart,
          ...customOptions?.chart,
        },
        title: {
          ...baseChartOptions.title,
          text: title || "",
          style: {
            ...baseChartOptions.title?.style,
            color: themeColors.title,
          },
          ...customOptions?.title,
        },
        subtitle: {
          ...baseChartOptions.subtitle,
          text: subtitle || "",
          style: {
            color: themeColors.subtitle,
          },
          ...customOptions?.subtitle,
        },
        legend: {
          ...baseChartOptions.legend,
          enabled: showLegend,
          itemStyle: {
            ...baseChartOptions.legend?.itemStyle,
            color: themeColors.labels,
          },
          itemHoverStyle: {
            color: themeColors.title,
          },
          ...customOptions?.legend,
        },
        tooltip: {
          ...baseChartOptions.tooltip,
          ...typeOptions.tooltip,
          enabled: showTooltip,
          ...customOptions?.tooltip,
        },
        xAxis: {
          ...(typeOptions.xAxis as Highcharts.XAxisOptions),
          labels: {
            ...(typeOptions.xAxis as Highcharts.XAxisOptions)?.labels,
            style: {
              color: themeColors.labels,
            },
          },
          title: {
            ...(typeOptions.xAxis as Highcharts.XAxisOptions)?.title,
            style: {
              color: themeColors.axisTitles,
            },
          },
          gridLineColor: themeColors.grid,
          lineColor: themeColors.grid,
          ...(customOptions?.xAxis as Highcharts.XAxisOptions),
        },
        yAxis: {
          ...(typeOptions.yAxis as Highcharts.YAxisOptions),
          labels: {
            ...(typeOptions.yAxis as Highcharts.YAxisOptions)?.labels,
            style: {
              color: themeColors.labels,
            },
          },
          title: {
            ...(typeOptions.yAxis as Highcharts.YAxisOptions)?.title,
            style: {
              color: themeColors.axisTitles,
            },
          },
          gridLineColor: themeColors.grid,
          ...(customOptions?.yAxis as Highcharts.YAxisOptions),
        },
        plotOptions: {
          ...baseChartOptions.plotOptions,
          ...typeOptions.plotOptions,
          ...customOptions?.plotOptions,
        },
      };

      return mergedOptions;
    }, [
      theme,
      title,
      subtitle,
      height,
      width,
      showLegend,
      showTooltip,
      seriesColors,
      themeColors,
      customOptions,
      chartProps,
    ]);

    return (
      <div className={twMerge(clsx("w-full", className))}>
        {topContent && (
          <div
            className={twMerge(
              clsx(
                "mb-4 flex items-center justify-end gap-2",
                topContentClassName,
              ),
            )}
          >
            {topContent}
          </div>
        )}
        <HighchartsReact
          ref={ref}
          highcharts={Highcharts}
          options={options}
          updateArgs={
            chartProps.type === "sankey" ? [true, true, true] : undefined
          }
        />
      </div>
    );
  },
);
